// packages
import cx from 'classnames';
import { h } from 'preact';

// components
import CandidateRow from './CandidateRow';
import Contest from './Contest';
import SeatPartyDivider from './SeatPartyDivider';
import Portal from './Portal';
import PureComponent from './PureComponent';
import Typeahead from './Typeahead';
import CountyInfo from './CountyInfo';

// internal
import debounce from '../utils/debounce';
import { determineContestTitleYourRaces } from '../election-utils';
import { lookup } from '../utils/legislative-districts';
import loadAssetURL from '../utils/load-asset-url';

const googleIMG = loadAssetURL('powered-by-google');

let PlacesLib = null; // { AutocompleteSessionToken, AutocompleteSuggestion, Place } loaded via importLibrary()

const bounds = {
  east: -93.514764,
  west: -106.662177,
  south: 25.843902,
  north: 36.500199,
};

const toLocationRestriction = b => ({
  west: b.west,
  east: b.east,
  south: b.south,
  north: b.north,
});

const getPredictions = async (input, sessionToken) => {
  if (!input || input.trim().length < 2) return [];

  const request = {
    input,
    locationRestriction: toLocationRestriction(bounds),
    includedPrimaryTypes: ['street_address', 'premise', 'route'],
    region: 'us',
    sessionToken,
  };

  const { suggestions } =
    await PlacesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(
      request
    );


  return suggestions.map(s => ({
    id: s.placePrediction.placeId,
    description: s.placePrediction.text.toString(),
    place_id: s.placePrediction.placeId,
    _prediction: s.placePrediction,
  }));
};

// always render what's provided, the geocoder decides
const shouldItemRender = () => true;

class YourRaces extends PureComponent {
  constructor() {
    super();

    this.state = {
      contests: [],
      coordinates: null,
      data: null,
      lookupError: false,
      lookupPending: false,
      lookedFoundStored: false,
      results: [],
      value: '',
    };

    this.handleGeocode = this.handleGeocode.bind(this);
    this.debouncedHandleGeocode = debounce(this.handleGeocode, 300);
    this.onChange = this.onChange.bind(this);
    this.onClear = this.onClear.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.sessionToken = null;
  }

  onClear(value) {
    this.setState({ value: '' });
  }

  onChange(value) {
    this.setState({ value }, this.debouncedHandleGeocode);
     // Start a new autocomplete session when the user begins typing
     if (!this.sessionToken && PlacesLib) {
      this.sessionToken = new PlacesLib.AutocompleteSessionToken();
    }
  }

  async handleGeocode() {
    if (!PlacesLib) return;
    const { value: input } = this.state;
    try {
      const results = await getPredictions(input, this.sessionToken);
      this.setState({ results });
    } catch (e) {
      this.setState({ results: [] });
    }
  
  }

  async onSelect({ name, placeId, _prediction }) {
    this.setState({ lookupError: false, lookupPending: true, value: name });

    try {
      // New flow: prediction -> Place -> fetchFields({ fields: ['location'] })
      const place = _prediction
        ? _prediction.toPlace()
        : new PlacesLib.Place({ id: placeId });

      await place.fetchFields({ fields: ['location'] }); // ends the session

      const latLng = place.location;
      const coordinates = {
        latitude:
          typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat,
        longitude:
          typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng,
      };

      // Reset session so the next keystroke starts a fresh token
      this.sessionToken = null;

      lookup(coordinates)
        .then(({ data }) => {
          this.setState({
            coordinates,
            data,
            lookupPending: false,
            lookedFoundStored: false,
          });
        })
        .catch(() =>
          this.setState({
            data: null,
            lookupError: true,
            lookupPending: false,
          })
        );
    } catch (e) {
      this.setState({
        data: null,
        lookupError: true,
        lookupPending: false,
      });
      this.sessionToken = null;
    }

        
  }

  async componentDidMount() {

    // New: load the modern Places library classes
    // eslint-disable-next-line no-undef
    PlacesLib = await google.maps.importLibrary('places');

    // // If we ever wanted to show a default location, coordinates for Governor mansion in Austin
    // let latitude = 30.2730665;
    // let longitude = -97.74341629999999;
    // let coordinates = { latitude, longitude };

    // lookup(coordinates)
    //   .then(({ data }) => {
    //     this.setState({
    //       coordinates,
    //       data,
    //       lookupPending: false,
    //       lookedFoundStored: false,
    //     });
    //   })
    //   .catch(() =>
    //     this.setState({
    //       data: null,
    //       lookupError: true,
    //       lookupPending: false,
    //     })
    //   );

  }

  render() {
    const { contests, notes } = this.props;
    const {
      coordinates,
      data,
      lookupError,
      lookupPending,
      results,
      value,
    } = this.state;

    let addressFeedback = null;

    let directoryUrl = `https://directory.texastribune.org/districts/`

    const matches = {
      'us-house': null,
      sboe: null,
      'tx-senate': null,
      'tx-house': null,
    };

    const noteMatches = {
      'us-house': null,
      sboe: null,
      'tx-senate': null,
      'tx-house': null,
    };


    if (data) {
      matches['us-house'] = contests.filter(
        contest =>
          contest.office === 'U.S. House' &&
          contest.office_division === data.us_house.district
      );

      matches['sboe'] = contests.filter(
        contest =>
          contest.office === 'State Board of Education' &&
          contest.office_division === data.sboe.district
      );

      matches['tx-senate'] = contests.filter(
        contest =>
          contest.office === 'Texas Senate' &&
          contest.office_division === data.tx_senate.district
      );

      matches['tx-house'] = contests.filter(
        contest =>
          contest.office === 'Texas House' &&
          contest.office_division === data.tx_house.district
      );

      noteMatches['us-house'] = notes.filter(
        contest =>
          contest.type ===
            'us-house-district-' + data.us_house.district
      );

      noteMatches['sboe'] = notes.filter(
        contest =>
          contest.type ===
            'state-board-of-education-district-' +
              data.sboe.district 
      );

      noteMatches['tx-senate'] = notes.filter(
        contest =>
          contest.type ===
            'texas-senate-district-' + data.tx_senate.district 
      );

      noteMatches['tx-house'] = notes.filter(
        contest =>
          contest.type ===
            'texas-house-district-' + data.tx_house.district
      );


     
    }

    if (lookupPending) {
      addressFeedback = (
        <p class="has-padding t-serif t-weight-bold t-align-center">
          Finding your details...{' '}
          <svg class="icon icon-spinner animate-pulse">
            <use xlinkHref="#icon-spinner" />
          </svg>
        </p>
      );
    }

    if (lookupError) {
      addressFeedback = (
        <p class="has-padding t-serif t-weight-bold t-align-center">
          Hmm... we were unable to find you in Texas. Please check your address
          and try again!
        </p>
      );
    }

    if (coordinates && !lookupPending && !lookupError) {
      addressFeedback = (
        <p class="has-padding t-serif t-weight-bold t-align-center">
          We found your information! These races have been personalized for you.
        </p>
      );
    }

    const inputProps = {
      id: 'address-input',
      class: 'address-input__input',
      placeholder: '1010 Colorado St, Austin, TX, 78701',
    };

    return (
      <div class="address-input t-sans">
        <svg class="icon icon-drawn-left-arrow">
          <use xlinkHref="#icon-drawn-left-arrow"></use>
        </svg>
        <label class="address-input__label" for="address-input">
          Enter your address
        </label>
        <Typeahead
          inputProps={inputProps}
          items={results}
          getItemValue={item => ({
            name: item.description,
            placeId: item.place_id,
            _prediction: item._prediction,
          })}
          value={value}
          onChange={this.onChange}
          onClear={this.onClear}
          onSelect={this.onSelect}
          renderItem={(item, isHighlighted) => (
            <li
              class={cx('listbox__item', {
                'listbox__item--highlighted': isHighlighted,
              })}
              key={item.id}
            >
              {item.description}
            </li>
          )}
          renderAttribution={() => (
            <li class="listbox__item listbox__item--attribution">
              <img src={googleIMG} alt="Powered by Google" />
            </li>
          )}
          shouldItemRender={shouldItemRender}
          renderInput={props => (
            <input
              {...props}
              ref={el => {
                props.ref(el);
                this.inputRef = el;
              }}
            />
          )}
        />
        {addressFeedback}
       
          {data && data.counties && (
             <Portal node={document.getElementById('local-candidates-info')}>
            <span>
              <CountyInfo
                key={data.counties.fips}
                county={data.counties.name}
                fips={data.counties.fips}
              />
            </span>
            </Portal>
          )}
       
        {data && contests.length > 0 && (
          <Portal
            node={document.getElementById('your-races-results')}
            ref={ref => (this._resultsPortal = ref)}
          >
            <div class="representative-districts contest-list">
              <Contest
                key={matches['us-house'][0].office_slug}
                contest={matches['us-house']}
                contestTitle={determineContestTitleYourRaces(
                  matches['us-house'][0]
                )}
                notes={noteMatches['us-house'].filter(
                  item => item.type === matches['us-house'][0].office_slug
                )}
                redistrictingNote 
                tableHeader={CandidateRow.Header}
                columnCount={CandidateRow.columnCount}
                renderParty={party => (
                  <SeatPartyDivider
                    partyName={party.party}
                    partyBallot={party}
                    tableHeader={CandidateRow.Header}
                    notes={noteMatches['us-house'].filter(
                      contest =>
                        contest.type === party.office_slug + '-' + party.party
                    )}
                    columnCount={CandidateRow.columnCount}
                    renderCandidate={(
                      candidate,
                      idx) => (
                      <CandidateRow
                        key={candidate.id}
                        ballotName={candidate.name}
                        candidateId={candidate.id}
                        isIncumbent={candidate.is_incumbent}
                        party={candidate.party}
                      />
                    )}
                  />
                )}
              />
              {matches['tx-senate'].length > 0 ? (
                <Contest
                  key={matches['tx-senate'].id}
                  contest={matches['tx-senate']}
                  tableHeader={CandidateRow.Header}
                  columnCount={CandidateRow.columnCount}
                  notes={noteMatches['tx-senate'].filter(
                    item => item.type === matches['tx-senate'][0].office_slug
                  )}
                  contestTitle={determineContestTitleYourRaces(
                    matches['tx-senate'][0]
                  )}
                  renderParty={party => (
                    <SeatPartyDivider
                      partyName={party.party}
                      partyBallot={party}
                      tableHeader={CandidateRow.Header}
                      notes={noteMatches['tx-senate'].filter(
                        contest =>
                          contest.type === party.office_slug + '-' + party.party
                      )}
                      columnCount={CandidateRow.columnCount}
                      renderCandidate={(
                        candidate,
                        idx) => (
                        <CandidateRow
                          key={candidate.id}
                          ballotName={candidate.name}
                          candidateId={candidate.id}
                          isIncumbent={candidate.is_incumbent}
                          party={candidate.party}
                        />
                      )}
                    />
                  )}
                />
              ) :
              <div class="seat"><span><h3 class="seat__title">Texas Senate {data['tx_senate']['district']} <a class="t-links-unset"
              href={`${directoryUrl}tx-senate/${data['tx_senate']['district']}/`}
            >
              <span class="icon-div" title="Click to learn more about this district">
                <svg class="icon icon-info">
                  <use xlinkHref="#icon-info"></use>
                </svg>
              </span>
            </a></h3></span>
              
              <div class="no_primary">Seat is not up for election this year.</div>
              </div>}
              <Contest
                key={matches['tx-house'].id}
                contest={matches['tx-house']}
                tableHeader={CandidateRow.Header}
                columnCount={CandidateRow.columnCount}
                notes={noteMatches['tx-house'].filter(
                  item => item.type === matches['tx-house'][0].office_slug
                )}
                contestTitle={determineContestTitleYourRaces(
                  matches['tx-house'][0]
                )}
                renderParty={party => (
                  <SeatPartyDivider
                    partyName={party.party}
                    partyBallot={party}
                    tableHeader={CandidateRow.Header}
                    notes={noteMatches['tx-house'].filter(
                      contest =>
                        contest.type === party.office_slug + '-' + party.party
                    )}
                    columnCount={CandidateRow.columnCount}
                    renderCandidate={(
                      candidate,
                      idx) => (
                      <CandidateRow
                        key={candidate.id}
                        ballotName={candidate.name}
                        candidateId={candidate.id}
                        isIncumbent={candidate.is_incumbent}
                        party={candidate.party}
                      />
                    )}
                  />
                )}
              />
              {matches['sboe'].length > 0 ? (
                <Contest
                  key={matches['sboe'][0].office_slug}
                  contest={matches['sboe']}
                  tableHeader={CandidateRow.Header}
                  columnCount={CandidateRow.columnCount}
                  notes={noteMatches['sboe'].filter(
                    item => item.type === matches['sboe'][0].office_slug
                  )}
                  contestTitle={determineContestTitleYourRaces(
                    matches['sboe'][0]
                  )}
                  renderParty={party => (
                    <SeatPartyDivider
                      partyName={party.party}
                      partyBallot={party}
                      tableHeader={CandidateRow.Header}
                      notes={noteMatches['sboe'].filter(
                        contest =>
                          contest.type === party.office_slug + '-' + party.party
                      )}
                      columnCount={CandidateRow.columnCount}
                      renderCandidate={(
                        candidate,
                        idx) => (
                        <CandidateRow
                          key={candidate.id}
                          ballotName={candidate.name}
                          candidateId={candidate.id}
                          isIncumbent={candidate.is_incumbent}
                          party={candidate.party}
                        />
                      )}
                    />
                  )}
                />
              ):
              <div class="seat"><span><h3 class="seat__title">State Board of Education {data['sboe']['district']} <a class="t-links-unset"
              href={`${directoryUrl}sboe/${data['sboe']['district']}`}
            >
              <span class="icon-div" title="Click to learn more about this district">
                <svg class="icon icon-info">
                  <use xlinkHref="#icon-info"></use>
                </svg>
              </span>
            </a></h3></span>
              
              <div class="no_primary">Seat is not up for election this year.</div>
              </div>
              }
            </div>
          </Portal>
        )}
      </div>
    );
  }
}

export default YourRaces;